import type {
  AdminCrewReportBulkRequestItem,
  AdminCrewReportBulkRequestRunStatus,
  AdminCrewReportRunningVesselCrewReport,
  UseAdminCrewReportBulkRequestsOptions,
} from "../models/admin-crew-report.models";
import {
  resolveAdminCrewReportBulkRequestRecipient,
  sendAdminCrewReportBulkRequest,
} from "../services/admin-crew-report.service";
import {
  getAdminCrewReportBulkRequestOutcome,
  updateAdminCrewReportBulkRequestItem,
} from "../utils/admin-crew-report-bulk-request.utils";
import { toAdminCrewReportVesselOption } from "../utils/admin-crew-report-running-vessels.utils";
import { getAdminCrewReportErrorMessage } from "../utils/admin-crew-report.utils";
import { useCallback, useEffect, useRef, useState } from "react";

export function useAdminCrewReportBulkRequests({
  onAttemptCompleted,
  onRunSettled,
  onStatusModalClosed,
}: UseAdminCrewReportBulkRequestsOptions) {
  const [confirmationVessels, setConfirmationVessels] = useState<AdminCrewReportRunningVesselCrewReport[] | null>(null);
  const [items, setItems] = useState<AdminCrewReportBulkRequestItem[]>([]);
  const [runStatus, setRunStatus] = useState<AdminCrewReportBulkRequestRunStatus>("idle");
  const [isStatusModalOpen, setIsStatusModalOpen] = useState(false);
  const queueRef = useRef<AdminCrewReportRunningVesselCrewReport[]>([]);
  const nextVesselIndexRef = useRef(0);
  const activeRunIdRef = useRef(0);
  const processingRef = useRef(false);
  const pauseAfterCurrentRef = useRef(false);
  const closeAfterCurrentRef = useRef(false);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;

    return () => {
      mountedRef.current = false;
      activeRunIdRef.current += 1;
      pauseAfterCurrentRef.current = true;
    };
  }, []);

  const closeStatusModal = useCallback(() => {
    setIsStatusModalOpen(false);
    setRunStatus("idle");
    setItems([]);
    queueRef.current = [];
    nextVesselIndexRef.current = 0;
    pauseAfterCurrentRef.current = false;
    closeAfterCurrentRef.current = false;
    onStatusModalClosed();
  }, [onStatusModalClosed]);

  const processQueue = useCallback(
    async (runId: number) => {
      if (processingRef.current) return;
      processingRef.current = true;

      try {
        while (mountedRef.current && activeRunIdRef.current === runId) {
          if (pauseAfterCurrentRef.current) {
            const settledStatus = nextVesselIndexRef.current >= queueRef.current.length ? "completed" : "paused";
            onRunSettled(settledStatus);
            if (closeAfterCurrentRef.current) closeStatusModal();
            else setRunStatus(settledStatus);
            break;
          }

          const vessel = queueRef.current[nextVesselIndexRef.current];
          if (!vessel) {
            setRunStatus("completed");
            onRunSettled("completed");
            break;
          }
          nextVesselIndexRef.current += 1;

          setItems((currentItems) => [
            ...currentItems,
            {
              vessel,
              recipientEmail: "",
              status: "resolvingRecipient",
              errorMessage: "",
            },
          ]);

          try {
            const requestVessel = toAdminCrewReportVesselOption(vessel);
            const recipient = await resolveAdminCrewReportBulkRequestRecipient(requestVessel);
            if (!mountedRef.current || activeRunIdRef.current !== runId) return;

            setItems((currentItems) =>
              updateAdminCrewReportBulkRequestItem(currentItems, vessel.imoNumber, {
                recipientEmail: recipient.email,
                status: "sendingRequest",
              }),
            );

            const response = await sendAdminCrewReportBulkRequest({
              vesselCode: vessel.vesselCode,
              imoNumber: vessel.imoNumber,
            });
            if (!mountedRef.current || activeRunIdRef.current !== runId) return;

            const outcome = getAdminCrewReportBulkRequestOutcome(response);
            setItems((currentItems) =>
              updateAdminCrewReportBulkRequestItem(currentItems, vessel.imoNumber, {
                recipientEmail: response.sentTo,
                status: outcome.status,
              }),
            );
            onAttemptCompleted(vessel.imoNumber, outcome.lifecycleStatus);
          } catch (error) {
            if (!mountedRef.current || activeRunIdRef.current !== runId) return;

            setItems((currentItems) =>
              updateAdminCrewReportBulkRequestItem(currentItems, vessel.imoNumber, {
                status: "failed",
                errorMessage: getAdminCrewReportErrorMessage(error),
              }),
            );
            onAttemptCompleted(vessel.imoNumber);
          }

          if (closeAfterCurrentRef.current || pauseAfterCurrentRef.current) {
            const settledStatus = nextVesselIndexRef.current >= queueRef.current.length ? "completed" : "paused";
            onRunSettled(settledStatus);
            if (closeAfterCurrentRef.current) closeStatusModal();
            else setRunStatus(settledStatus);
            break;
          }
        }
      } finally {
        processingRef.current = false;
      }
    },
    [closeStatusModal, onAttemptCompleted, onRunSettled],
  );

  const openConfirmation = useCallback((vessels: AdminCrewReportRunningVesselCrewReport[]) => {
    if (vessels.length === 0) return;
    setConfirmationVessels(vessels);
  }, []);

  const closeConfirmation = useCallback(() => setConfirmationVessels(null), []);

  const start = useCallback(() => {
    if (!confirmationVessels?.length || processingRef.current) return;

    const runId = activeRunIdRef.current + 1;
    activeRunIdRef.current = runId;
    queueRef.current = confirmationVessels;
    nextVesselIndexRef.current = 0;
    pauseAfterCurrentRef.current = false;
    closeAfterCurrentRef.current = false;
    setConfirmationVessels(null);
    setItems([]);
    setRunStatus("running");
    setIsStatusModalOpen(true);
    void processQueue(runId);
  }, [confirmationVessels, processQueue]);

  const stop = useCallback(() => {
    if (runStatus !== "running") return;
    pauseAfterCurrentRef.current = true;
    setRunStatus("stopping");
  }, [runStatus]);

  const resume = useCallback(() => {
    if (runStatus !== "paused") return;
    pauseAfterCurrentRef.current = false;
    setRunStatus("running");
    void processQueue(activeRunIdRef.current);
  }, [processQueue, runStatus]);

  const requestClose = useCallback(() => {
    if (runStatus === "running" || runStatus === "stopping") {
      closeAfterCurrentRef.current = true;
      pauseAfterCurrentRef.current = true;
      setRunStatus("closing");
      return;
    }

    activeRunIdRef.current += 1;
    closeStatusModal();
  }, [closeStatusModal, runStatus]);

  return {
    confirmationVessels,
    items,
    runStatus,
    isStatusModalOpen,
    isWorkflowActive: Boolean(confirmationVessels) || isStatusModalOpen,
    openConfirmation,
    closeConfirmation,
    start,
    stop,
    resume,
    requestClose,
  };
}
